import React, { useState, useEffect, useRef } from "react";
import { Link } from "./L";
import { Outlet, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import axios from "axios";
import { useLanguage, languages } from "../i18n/LanguageContext";
import PageTransition from "./PageTransition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/api/files/local/logo-normal.png";
const LOGO_WHITE_URL = "/api/files/local/logo-white.png";
const FOOTER_LOGO_URL = "/api/files/local/logo-clean.png";
const ICLEAR_LOGO_URL = "/api/files/local/iclear-white.png";

const getNavLinks = (t) => [
  { label: t("nav_about"), href: "/rreth-nesh" },
  { label: t("nav_events"), href: "/evente" },
  { label: t("nav_trainers"), href: "/trajnere" },
  { label: t("nav_gallery"), href: "/galeria" },
  { label: t("nav_blog"), href: "/blog" },
  { label: t("nav_contact"), href: "/kontakt" },
];

export const LanguageSelector = ({ isLight }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find((l) => l.code === lang) || languages[0];

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors text-sm ${isLight ? "text-white/80 hover:text-white" : "text-[#003F72] hover:text-[#1CA3B0]"}`}
        data-testid="language-selector-btn"
      >
        <span className={`fi fi-${current.flag} rounded-sm shadow-sm`} style={{ width: 20, height: 15 }} aria-hidden="true" />
        <span className="font-semibold">{current.label}</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 py-2 min-w-[160px] z-[100]" data-testid="language-dropdown">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${l.code === lang ? "bg-[#1CA3B0]/5 text-[#08707A] font-semibold" : "text-[#4A5568]"}`}
              data-testid={`lang-${l.code}`}
            >
              <span className={`fi fi-${l.flag} rounded-sm shadow-sm`} style={{ width: 20, height: 15 }} aria-hidden="true" />
              <span className="font-semibold w-6">{l.label}</span>
              <span>{l.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useLanguage();
  const navLinks = getNavLinks(t);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";
  const isTransparent = isHome && !scrolled;

  return (
    <>
      {/* Top bar */}
      <div
        className={`text-sm py-2 px-6 hidden md:block transition-all duration-500 ease-in-out ${
          isHome ? 'fixed left-0 right-0 z-[51]' : 'bg-white border-b border-slate-100 relative'
        }`}
        style={isHome ? {
          top: 0,
          background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
          borderBottom: scrolled ? '1px solid rgba(226,232,240,0.5)' : 'none',
          transform: scrolled ? 'translateY(-100%)' : 'translateY(0)',
          opacity: scrolled ? 0 : 1,
        } : {}}
        data-testid="top-bar"
      >
        <div className="max-w-[1350px] mx-auto flex justify-end items-center gap-6">
          <a href="mailto:info@aligners.academy" className={`flex items-center gap-2 transition-colors duration-500 ${isTransparent ? 'text-white/80 hover:text-white' : 'text-[#003F72] hover:text-[#1CA3B0]'}`}>
            <i className="fas fa-envelope text-xs"></i> info@aligners.academy
          </a>
          <a href="tel:+355696056794" className={`flex items-center gap-2 transition-colors duration-500 ${isTransparent ? 'text-white/80 hover:text-white' : 'text-[#003F72] hover:text-[#1CA3B0]'}`}>
            <i className="fas fa-phone text-xs"></i> +355 696056794
          </a>
        </div>
      </div>

      {/* Main nav */}
      <header
        className={`${isHome ? 'fixed' : 'sticky'} left-0 right-0 z-50 transition-all duration-500 ease-in-out ${!isHome ? 'glass-nav scrolled' : ''}`}
        style={
          isHome
            ? {
                top: scrolled ? '0px' : '36px',
                background: scrolled ? 'rgba(255,255,255,0.97)' : 'transparent',
                backdropFilter: scrolled ? 'blur(20px)' : 'none',
                WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
                boxShadow: scrolled ? '0 2px 16px rgba(0,0,0,0.08)' : 'none',
                borderBottom: scrolled ? '1px solid rgba(226,232,240,0.4)' : 'none',
              }
            : { top: 0 }
        }
        data-testid="main-header"
      >
        <div className="max-w-[1350px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={isTransparent ? LOGO_WHITE_URL : LOGO_URL} alt="Albanian Aligners Academy" className="h-14 w-auto object-contain transition-opacity duration-500" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                className={`relative transition-colors duration-500 py-1 ${
                  location.pathname === link.href
                    ? isTransparent ? 'text-white' : 'text-[#1CA3B0]'
                    : isTransparent ? 'text-white/80 hover:text-white' : 'text-[#003F72] hover:text-[#1CA3B0]'
                }`}
                style={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: '16px' }}
              >
                {link.label}
                {location.pathname === link.href && (
                  <span className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-colors duration-500 ${isTransparent ? 'bg-white' : 'bg-[#1CA3B0]'}`} />
                )}
              </Link>
            ))}
            <LanguageSelector isLight={isTransparent} />
          </nav>

          {/* Mobile toggle */}
          <button
            className={`lg:hidden p-2 transition-colors duration-500 ${isTransparent ? 'text-white' : 'text-[#08707A]'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-200 shadow-xl z-40" data-testid="mobile-menu">
            <nav className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.href.replace('/', '') || 'home'}`}
                  className={`text-base font-semibold py-2 border-b border-slate-100 ${
                    location.pathname === link.href ? 'text-[#08707A]' : 'text-[#4A5568]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3 flex items-center gap-2 flex-wrap">
                {languages.map((l) => (
                  <button key={l.code} onClick={() => setLang(l.code)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-sm ${l.code === lang ? 'bg-[#1CA3B0]/10 text-[#08707A] font-semibold' : 'text-[#4A5568] hover:bg-slate-100'}`} data-testid={`mobile-lang-${l.code}`}>
                    <span className={`fi fi-${l.flag} rounded-sm`} style={{ width: 16, height: 12 }} aria-hidden="true" />
                    {l.label}
                  </button>
                ))}
              </div>
              <div className="pt-2 flex flex-col gap-2 text-sm text-[#4A5568]">
                <a href="mailto:info@aligners.academy" className="flex items-center gap-2">
                  <i className="fas fa-envelope text-xs"></i> info@aligners.academy
                </a>
                <a href="tel:+355696056794" className="flex items-center gap-2">
                  <i className="fas fa-phone text-xs"></i> +355 696056794
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

const Footer = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const { t, tx } = useLanguage();
  const navLinks = getNavLinks(t);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        const upcoming = res.data.filter(e => e.category === "upcoming").slice(0, 2);
        setUpcomingEvents(upcoming);
      } catch (err) {
        console.error(err);
      }
    };
    fetchEvents();
  }, []);

  return (
    <footer className="text-white" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)', fontFamily: "'Noto Sans', sans-serif" }} data-testid="footer">
      <div className="max-w-[1350px] mx-auto px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img src={FOOTER_LOGO_URL} alt="Albanian Aligners Academy" className="h-20 w-auto mb-6" />
            <p className="text-white text-sm leading-relaxed mb-6">
              {t("footer_brand")}
            </p>
            <div className="flex items-center gap-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/25 transition-colors text-white" aria-label="Facebook" data-testid="footer-facebook"><i className="fab fa-facebook-f"></i></a>
              <a href="https://www.instagram.com/albanian.aligners.academy/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/25 transition-colors text-white" aria-label="Instagram" data-testid="footer-instagram"><i className="fab fa-instagram"></i></a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/25 transition-colors text-white" aria-label="X" data-testid="footer-twitter"><i className="fab fa-x-twitter"></i></a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/25 transition-colors text-white" aria-label="LinkedIn" data-testid="footer-linkedin"><i className="fab fa-linkedin-in"></i></a>
            </div>
          </div>

          {/* Menu */}
          <div>
            <h4 className="font-semibold text-lg mb-3 text-white">{t("footer_menu")}</h4>
            <hr className="border-white/30 mb-5" />
            <ul className="space-y-3">
              {navLinks.filter(link => link.href !== "/").map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-white hover:text-gray-300 transition-colors text-sm" data-testid={`footer-link-${link.href.replace('/', '') || 'home'}`}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Eventet e Ardhshme */}
          <div>
            <h4 className="font-semibold text-lg mb-3 text-white">
              <i className="fas fa-calendar-alt mr-2"></i>{t("footer_upcoming")}
            </h4>
            <hr className="border-white/30 mb-5" />
            {upcomingEvents.length > 0 ? (
              <div className="space-y-4">
                {upcomingEvents.map((event) => (
                  <Link to={`/evente/${event.slug || event.id}`} key={event.id} className="block hover:opacity-80 transition-opacity">
                    <p className="text-white text-sm font-medium leading-snug">{event.title}</p>
                    <p className="text-white font-bold text-sm mt-1">{event.date}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-gray-300 text-sm">{t("footer_no_events")}</p>
            )}
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="font-semibold text-lg mb-3 text-white">{t("footer_contact")}</h4>
            <hr className="border-white/30 mb-5" />
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-white">
                <i className="fas fa-map-marker-alt mt-1 text-white"></i>
                <span>{tx("iClear Group, Rruga Albanopoli 43, 1016, Tirane, Shqiperi")}</span>
              </li>
              <li>
                <a href="tel:+355696056794" className="flex items-center gap-3 text-sm text-white hover:text-gray-300 transition-colors">
                  <i className="fas fa-phone text-white"></i> +355 696056794
                </a>
              </li>
              <li>
                <a href="mailto:info@aligners.academy" className="flex items-center gap-3 text-sm text-white hover:text-gray-300 transition-colors">
                  <i className="fas fa-envelope text-white"></i> info@aligners.academy
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white text-sm text-center md:text-left [overflow-wrap:anywhere]">
            &copy;{new Date().getFullYear()} - {t("footer_rights")} | <strong>Albanian Aligners Academy</strong>
            <span className="mx-2 text-white/60">·</span>
            <Link to="/privatesia" className="text-white hover:text-gray-300 underline-offset-2 hover:underline" data-testid="footer-privacy-link">
              {t("contact_privacy_link")}
            </Link>
            <span className="mx-2 text-white/60">·</span>
            <Link to="/kushtet" className="text-white hover:text-gray-300 underline-offset-2 hover:underline" data-testid="footer-terms-link">
              {tx("Kushtet e Përdorimit")}
            </Link>
          </p>
          <div className="flex items-center gap-2">
            <span className="text-white text-sm">{t("footer_powered")}</span>
            <a href="https://icleargroup.com" target="_blank" rel="noopener noreferrer">
              <img src={ICLEAR_LOGO_URL} alt="iClear" className="h-8 w-auto" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const WhatsAppFloat = () => (
  <a
    href="https://wa.me/355696056794"
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    data-testid="whatsapp-float"
    aria-label="WhatsApp"
  >
    <MessageCircle size={28} />
  </a>
);

const Layout = () => (
  <>
    <Header />
    <main>
      <PageTransition>
        <Outlet />
      </PageTransition>
    </main>
    <Footer />
    <WhatsAppFloat />
  </>
);

export default Layout;
